/**
 * src/lib/api.ts
 * =============================================================================
 * Cliente HTTP central contra la API (Node + Hono en el VPS de OVH desde la
 * migración de septiembre de 2026; antes era un Worker de Cloudflare).
 *
 * Todas las llamadas al backend pasan por aquí:
 *  - Base URL leída de `VITE_API_BASE_URL` (= `https://api.ploter.me` en prod,
 *    `http://localhost:3000` en dev contra `pnpm dev` de `backend/`).
 *  - `credentials: 'include'` en cada request → manda las cookies HTTP-only
 *    de Better Auth (frontend en ploter.me + backend en api.ploter.me son
 *    SAME-SITE porque comparten eTLD+1, así SameSite=Lax basta).
 *  - Error shape coherente con lo que devuelve la API:
 *    `{ error: 'code', details?: {...} }` → la `ApiError` que lanzamos lleva
 *    `code` para que la UI traduzca por i18n.
 *
 * No hay token JWT en headers: el backend identifica al usuario por la
 * cookie. Si la cookie expira o no existe → 401, el caller decide qué hacer
 * (típicamente: forzar logout / redirigir a /login).
 * =============================================================================
 */

import * as Sentry from '@sentry/react'
import { env } from '@/lib/env'

/** Error de la API. `code` es semántico y traducible vía i18n. */
export class ApiError extends Error {
  code: string
  status: number
  details?: Record<string, unknown>
  constructor(code: string, status: number, details?: Record<string, unknown>) {
    super(code)
    this.code = code
    this.status = status
    this.details = details
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  /** Pasa `false` para NO mandar cookies (rutas públicas o de auth pública). */
  credentials?: boolean
  /** Headers extras (Content-Type ya se pone solo si hay body JSON). */
  headers?: Record<string, string>
  signal?: AbortSignal
}

/**
 * Cuántos avisos de "no llego a la API" se mandan por pestaña abierta.
 *
 * El tope no es tacañería: alguien en un tren pierde la cobertura veinte veces
 * en diez minutos, y sin límite eso son veinte avisos de un problema que no es
 * nuestro. Con tres basta para que un fallo real se vea, y para que uno
 * pasajero no tape a los demás.
 */
const MAX_AVISOS_DE_RED = 3

/**
 * Cuánto se espera a la API antes de rendirse.
 *
 * Sin límite, `fetch` espera lo que decida el sistema operativo — que pueden
 * ser minutos. Y como la pantalla no se pinta hasta que la primera llamada
 * termina, eso convertía una API inalcanzable en una aplicación que se queda
 * cargando para siempre, sin decir nada y sin salida. Se vio el 2026-08-23.
 *
 * Quince segundos son de sobra para cualquier llamada de esta aplicación (la
 * más lenta comprueba un objeto en R2) y siguen siendo razonables para alguien
 * con una conexión mala. Las subidas de archivos NO pasan por aquí: usan otro
 * mecanismo porque necesitan barra de progreso.
 */
const TIEMPO_MAXIMO_MS = 15_000
let avisosMandados = 0

/**
 * Manda a Sentry que la API no contesta.
 *
 * Se agrupan todos bajo la misma huella para que sean UN problema con muchos
 * casos y no cien problemas distintos: lo que hay que ver de un vistazo es
 * "esto le está pasando a mucha gente", no una lista interminable.
 */
function avisarDeApiInalcanzable(path: string, causa: unknown): void {
  if (avisosMandados >= MAX_AVISOS_DE_RED) return
  avisosMandados += 1
  Sentry.captureException(causa instanceof Error ? causa : new Error('api_inalcanzable'), {
    fingerprint: ['api-inalcanzable'],
    tags: { ruta: path },
    extra: {
      // De qué host hablamos: distingue "se ha caído la API" de "este usuario
      // no tiene internet", que se parecen mucho desde aquí.
      destino: env.apiBaseUrl,
      enLinea: typeof navigator !== 'undefined' ? navigator.onLine : 'desconocido',
    },
  })
}

/**
 * Llamada HTTP a la API. Devuelve el JSON parseado o lanza ApiError.
 * El path es relativo a `VITE_API_BASE_URL` y debe empezar con `/`.
 */
export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const url = `${env.apiBaseUrl}${path}`
  const method = opts.method ?? 'GET'
  const headers: Record<string, string> = { ...opts.headers }
  const init: RequestInit = {
    method,
    headers,
    credentials: opts.credentials === false ? 'omit' : 'include',
    // Siempre hay un límite. Si quien llama trae el suyo, manda el suyo.
    signal: opts.signal ?? AbortSignal.timeout(TIEMPO_MAXIMO_MS),
  }
  if (opts.body !== undefined) {
    headers['content-type'] = 'application/json'
    init.body = JSON.stringify(opts.body)
  }

  let res: Response
  try {
    res = await fetch(url, init)
  } catch (e) {
    // Que la API no conteste se AVISA, no solo se traduce a un mensaje.
    //
    // Este catch existe para que la aplicación no reviente, y eso está bien.
    // El efecto secundario es que Sentry nunca se entera: su SDK solo recoge
    // sola los errores NO capturados, y aquí se captura. Es el mismo patrón que
    // escondía los fallos del cron y los del manejador global del Worker.
    //
    // Y aquí duele más que en ningún otro sitio, porque cuando la API no
    // responde NINGÚN control del servidor puede avisar: las peticiones no
    // llegan, así que los paneles enseñan cero tráfico — que se ve exactamente
    // igual que "ahora mismo no hay nadie usándolo". Una caída que hace que la
    // vigilancia parezca tranquila. El navegador de quien lo sufre es el único
    // testigo que existe.
    //
    // Ocurrió el 2026-08-23: tres episodios en dos días con la API inaccesible
    // desde el operador más grande de España, y ni un aviso.
    avisarDeApiInalcanzable(path, e)
    throw new ApiError('network_error', 0, { message: e instanceof Error ? e.message : 'unknown' })
  }

  // Body puede ser JSON o texto vacío (ej. en 204). Intentamos JSON; si falla,
  // tratamos como texto plano.
  let data: unknown
  const text = await res.text()
  if (text.length > 0) {
    try { data = JSON.parse(text) } catch { data = text }
  }

  if (!res.ok) {
    if (data && typeof data === 'object' && 'error' in data) {
      const d = data as { error: string; details?: Record<string, unknown>; message?: string }
      throw new ApiError(d.error, res.status, d.details)
    }
    if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
      // Better Auth devuelve este shape: { code: 'VALIDATION_ERROR', message: '...' }
      const d = data as { code: string; message: string }
      throw new ApiError(d.code, res.status, { message: d.message })
    }
    throw new ApiError(`http_${res.status}`, res.status)
  }

  return data as T
}

/** URL absoluta a la API — útil para WebSockets o presigned URLs externas. */
export function apiUrl(path: string): string {
  return `${env.apiBaseUrl}${path}`
}

/**
 * Construye una URL WebSocket equivalente al path indicado. Cambia http→ws
 * y https→wss según el protocolo de la API.
 */
export function wsUrl(path: string): string {
  const base = env.apiBaseUrl.replace(/^http/i, 'ws')
  return `${base}${path}`
}
